import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo } from 'react';
import { useLoggedGeladaLazyQuery, type IUser } from '@netfront/gelada-identity-library';
import { getAccessTokenCookie } from '@netfront/common-library';
import { gql } from '@apollo/client';
import { EProcessState } from '@/models/enums';

interface UserContextType {
  // Indicates the user has an access token, but it may be expired, or for a different project.
  hasAccessToken: boolean;

  // The currently logged in user.
  user: IUser | undefined;

  // A computed value indicating the state of fetching the user.
  fetchUserState: EProcessState;

  // Functions to update parts of the user locally without refetching.
  localUpdates: {
    updateNames: (args: { firstName: string; lastName: string }) => void;
    updateEmail: (args: { email: string }) => void;
    updateProfileImage: (args: { presignedUrl: string | undefined }) => void;
    updateUserMfa: (args: { hasMfa: boolean }) => void;
  };
  refetchUser: () => Promise<unknown>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const GET_USER_QUERY = gql`
    query {
      user {
        getLoggedUser {
          id
          firstName: firstname
          lastName: lastname
          credential {
            email
            phoneNumber
          }
          hasMfa
          status
          isProjectAdmin(projectId: "${String(process.env.REACT_APP_PROJECT_ID)}")
          pictures {
            originalPicture {
              presignedUrl
            }
            profilePicture {
              presignedUrl
            }
          }
          memberships {
            id
            groupId
            userType {
              id
            }
          }
        }
      }
    }
    `;

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const accessToken = getAccessTokenCookie();
  const [user, setUser] = useState<IUser | undefined>(undefined);

  const [getUser, { loading, error, refetch, data }] = useLoggedGeladaLazyQuery<{ user: { getLoggedUser: IUser } }, {}>({
    query: GET_USER_QUERY,
  });

  // Fetch the user data when the access token changes
  useEffect(() => {
    if (!accessToken) {
      setUser(undefined);
      return;
    }

    getUser();
  }, [accessToken]);

  useEffect(() => {
    if (data?.user.getLoggedUser) {
      setUser(data.user.getLoggedUser);
    }
  }, [data?.user.getLoggedUser]);

  // A helper func to add type safety when updating the user
  function setNonNullUser(func: (user: IUser) => IUser) {
    if (user) {
      setUser(() => {
        return func(user);
      });
    } else {
      console.error('Attempted to update the user when there is no user to update. (It is undefined)');
      return;
    }
  }

  const fetchUserState = useMemo(() => {
    if (loading) return EProcessState.Loading;
    if (error) return EProcessState.Error;
    if (user) return EProcessState.Completed;
    return EProcessState.Idle;
  }, [loading, error, user]);

  const value = useMemo<UserContextType>(
    () => ({
      hasAccessToken: Boolean(accessToken),
      user,
      fetchUserState,
      localUpdates: {
        updateNames: ({ firstName, lastName }) => setNonNullUser((u) => ({ ...u, firstName, lastName })),
        updateEmail: ({ email }) => setNonNullUser((u) => ({ ...u, credential: { ...u.credential, email } })),
        updateProfileImage: ({ presignedUrl }) =>
          setNonNullUser((u) => ({
            ...u,
            pictures: {
              ...u.pictures,
              originalPicture: presignedUrl ? { presignedUrl } : undefined,
              profilePicture: presignedUrl ? { presignedUrl } : undefined,
            } as IUser['pictures'],
          })),
        updateUserMfa: ({ hasMfa }) => setNonNullUser((u) => ({ ...u, hasMfa })),
      },
      refetchUser: refetch,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken, user, fetchUserState, refetch],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};
